/**
 * Note: See this reference for the `as const` assertions which
 * effectively mark arrays `readonly`. We use this property to create
 * union types for the supported locales and countries.
 *
 * https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-4.html#const-assertions
 *
 * This is a nice little shorthand but it has its downsides. For one,
 * you have to cast them properly to use any array methods. E.g.
 *
 *    (SUPPORTED_LOCALES as ReadonlyArray<string>).includes(myLocale)
 *
 * I don't think that's terrible given the benefits this gives us. Send
 * that MR if you disagree or if you think this is overkill (first think
 * about how it makes the _developers_ lives easy, not yours.)
 */

// The wildcard symbol in the route that represents "redirect me to the first valid operation"
export const WILDCARD_OPERATION_ID = "--";

// Given `/<some-app>/operations/<operationId>`, if `operationId` is `add`,
// we will treat it as a route instead of an operation id.
export const ADD_OPERATION = "add";

// The DOM element ID of the React portal in the nav bar, used to allow apps to
// inject a React component into the Nav.
export const NAV_PORTAL_ROOT_ID = "fabric-nav-portal-root";

// This localStorage key shouldn't be changed because is being used by Insights
// for a while, this is the Insights Tab notification badge.
export const NAV_NOTIFICATIONS_INSIGHTS_KEY = "insightsViewed";

// This is the enrollment key for cropvisor.  Use the feature ID contained
// in the enrollments return object to determine whether the link
// to cropvisor should be displayed.
export const NAV_CROPVISOR_ENROLLED_FEATURE_ID =
  "3d5111eb-292c-4f06-ad2d-d915ee59b312";

/**
 * Locales we support. Upstream services store locale in lower-case
 * (e.g. "en-us".) In the Core, we store and use locales in the standard
 * format (e.g. "en-US".)
 */
export const DEFAULT_LOCALE = "en-US";
export const SUPPORTED_LOCALES = [
  DEFAULT_LOCALE,
  "pt-BR",
  "en-CA",
  "fr-CA",
] as const;

/**
 * Where we've deployed our web applications. For example,
 *
 *    https://us.foo.granular.ag
 *            ^
 *
 *    https://br.dev.bar.granular.ag
 *            ^
 *
 * These are in lowercase because the only context in which they're used
 * is the URI.
 *
 * Yes, you _could_ use the `SUPPORTED_LOCALES` list above to generate
 * this but why do that on the client's browser? Use a test to ensure we
 * have at least one locale per country and keep it simple here 🤗
 */
export const DEFAULT_COUNTRY = "us";
export const SUPPORTED_COUNTRIES = [DEFAULT_COUNTRY, "br", "ca"] as const;

/**
 * These are the supported countries in uppercase. We use this to
 * convert the values from the roles service v4 endpoint.
 */
type ToUpperCase<T extends string> = Uppercase<T>;
export type SupportedCountry = ToUpperCase<typeof SUPPORTED_COUNTRIES[number]>;
// Example: SupportedCountry is now "US" | "BR" | "CA"

/**
 * Making "region" synonymous with "country" because quite a few
 * services use the former term. This is because we just took 'region'
 * from 'AWS Region' without bothering to think about what it really
 * means to us outside of AWS' context 🤷‍♂️✨
 *
 * For example, if we have customers in Argentina, we would presumably
 * have an https://ar.granular.ag which would be hosted in the `br` AWS
 * Region. Region and country are not synonymous. But we, at Granular,
 * mean "country" when we say "region." Lovely, no? Names matter.
 */
export const DEFAULT_REGION = DEFAULT_COUNTRY;

export * from "./platforms";
export * from "./applications";
